import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, iif, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AsiCustomerDto } from '@myasi/api/myasi-auth/permissions/models/asi-customer-dto';
import { AsiUserDto } from '@myasi/api/myasi-auth/permissions/models/asi-user-dto';

import { isDefined } from '@mysvg/utils/utils/general.utils';

import { ContextState } from '../../context/enums/context-state.enum';
import { AsiContext } from '../../context/models/asi-context.model';
import { DefaultUserTokenClaims } from '../../context/models/user-token-claims.model';
import { AuthRepositoryService } from '../repositories/auth-repository.service';
import { AuthenticationService } from './authentication.service';
import { OauthWrapperService } from './oauth-wrapper.service';

@Injectable({ providedIn: 'root' })
export class AsiAuthenticationService extends AuthenticationService<AsiContext> {
	private authRepositoryService = inject(AuthRepositoryService);
	private oauthWrapperService = inject<OauthWrapperService<DefaultUserTokenClaims>>(OauthWrapperService);
	private static readonly uuidV4Pattern = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

	notLoggedIn(): AsiContext {
		return new AsiContext([], ContextState.NOT_LOGGED_IN);
	}

	/**
	 * [NOTE] failed `getDecodedToken` or `getUser` will result by logout (done by mac)
	 * 				failed `getCustomer` will result in staff context and error message (handled by `authRepositoryService`)
	 */
	postLoginFlow(contextId: string | null): Observable<AsiContext> {
		return forkJoin([this.oauthWrapperService.getDecodedToken(), this.authRepositoryService.getUser(), this.getCustomer(contextId)]).pipe(
			map(([userTokenClaims, user, customer]: [DefaultUserTokenClaims, AsiUserDto, AsiCustomerDto]) =>
				this.createContext(userTokenClaims, user, customer),
			),
		);
	}

	private getCustomer(contextId: string | null): Observable<AsiCustomerDto> {
		// id must either be a number or an uuid, the endpoint supports both variants
		return iif(
			() => !isNaN(Number.parseInt(contextId, 10)) || this.isUUID(contextId),
			this.authRepositoryService.getCustomer(contextId),
			of(null),
		);
	}

	private isUUID(value: string): boolean {
		return AsiAuthenticationService.uuidV4Pattern.test(value);
	}

	private createContext(userTokenClaims: DefaultUserTokenClaims, user: AsiUserDto, customer?: AsiCustomerDto): AsiContext {
		const contextState = this.getContextState(userTokenClaims, user, customer);

		return new AsiContext(
			user.activities,
			contextState,
			// optionals
			user.asdRegistration,
			customer,
			user.email,
			user.firstname,
			user.id,
			user.lastname,
			user.level,
			user.projects,
			user.asiSvgInfoDto,
			user.userName,
		);
	}

	/**
	 * [NOTE] create context by token, user info and customer info
	 *
	 * [CAUTION] if there will be customer users, those checks must be extended by checking user for staff or customer
	 */
	private getContextState(userTokenClaims: DefaultUserTokenClaims, user: AsiUserDto, customer?: AsiCustomerDto): ContextState {
		if (isDefined(userTokenClaims) && isDefined(user) && isDefined(customer)) {
			return ContextState.STAFF_CUSTOMER;
		} else if (isDefined(userTokenClaims) && isDefined(user)) {
			return ContextState.STAFF;
		} else {
			return ContextState.NOT_LOGGED_IN;
		}
	}
}
